import { useCallback, useRef, useState } from "react";
import { FaInstagram, FaLinkedinIn } from "react-icons/fa6";
import "./Thumbnail.css";
import { Carousel } from "../../components";

const Thumbnail = ({ ogData, variant = "default" }) => {
  const cardRef = useRef(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const isTeamMemberVariant = variant === "team-member";
  const isGlassVariant = variant === "team" || isTeamMemberVariant;

  // Helper to ensure URLs are absolute
  const getAbsoluteUrl = (url) => {
    if (!url) return null;

    // Already absolute (external URLs from OpenGraph)
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    // Relative path (from database) - prepend API base URL
    if (url.startsWith("/")) {
      const apiBaseUrl =
        process.env.REACT_APP_API_URL || "http://localhost:8888";
      return `${apiBaseUrl}${url}`;
    }

    // Otherwise assume it's relative and needs base URL with separator
    const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:8888";
    return `${apiBaseUrl}/${url}`;
  };

  const handleClick = useCallback((event) => {
    if (event.target.closest("[data-no-flip='true']")) {
      return;
    }

    if (isTeamMemberVariant) {
      setIsFlipped((current) => !current);
      return;
    }

    if (ogData.url) {
      window.open(ogData.url, "_blank", "noopener,noreferrer");
    }
  }, [isTeamMemberVariant, ogData.url]);

  const getImages = () => {
    let imageList = [];

    // Check 'images' (plural) first - used by news items
    if (ogData.images && Array.isArray(ogData.images)) {
      imageList = ogData.images.filter((img) => img);
    }
    // Then check 'image' (singular) - used by team members
    else if (ogData.image) {
      if (Array.isArray(ogData.image)) {
        imageList = ogData.image.filter((img) => img);
      } else {
        imageList = [ogData.image];
      }
    }

    // Convert all URLs to absolute and filter out any nulls
    return imageList.map((url) => getAbsoluteUrl(url)).filter((url) => url);
  };

  const images = getImages();
  const hasMultipleImages = images.length > 1;

  const carouselItems = images.map((img, index) => ({
    id: `${ogData.title}-${index}`,
    url: img,
    type: "image",
  }));
  const linkedinUrl = ogData.linkedinUrl || ogData.url || "";
  const instagramUrl = ogData.instagramUrl || "";

  const handlePointerMove = useCallback(
    (e) => {
      if (!isGlassVariant || !cardRef.current) return;

      const card = cardRef.current;
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const rotateX = (y - centerY) / 15;
      const rotateY = (centerX - x) / 14;

      card.style.setProperty("--pointer-x", `${x}px`);
      card.style.setProperty("--pointer-y", `${y}px`);
      card.style.setProperty("--holo-bg-x", `${(x / rect.width) * 100}%`);
      card.style.setProperty("--holo-bg-y", `${(y / rect.height) * 100}%`);
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-3px)`;
    },
    [isGlassVariant]
  );

  const resetPointerState = useCallback(() => {
    if (!isGlassVariant || !cardRef.current) return;

    const card = cardRef.current;
    card.style.transform =
      "perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0)";
    card.style.setProperty("--pointer-x", "50%");
    card.style.setProperty("--pointer-y", "50%");
    card.style.setProperty("--holo-bg-x", "50%");
    card.style.setProperty("--holo-bg-y", "50%");
  }, [isGlassVariant]);

  const handleKeyDown = useCallback(
    (event) => {
      if (!isTeamMemberVariant) return;

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setIsFlipped((current) => !current);
      }
    },
    [isTeamMemberVariant]
  );

  const handleSocialLinkClick = useCallback((event) => {
    event.stopPropagation();
    event.nativeEvent?.stopImmediatePropagation?.();
  }, []);

  if (!ogData) return null;

  const renderMedia = () =>
    hasMultipleImages ? (
      <div className="thumbnail-carousel-wrapper">
        <Carousel
          mediaItems={carouselItems}
          showIndicators={true}
          className="thumbnail-carousel"
        />
      </div>
    ) : (
      images[0] && <img src={images[0]} alt="" className="thumbnail-image" />
    );

  return (
    <div
      ref={cardRef}
      className={`thumbnail-container${
        isGlassVariant ? " thumbnail-container--team-holographic" : ""
      }${isTeamMemberVariant ? " thumbnail-container--team-member" : ""}${
        isFlipped ? " is-flipped" : ""
      }`}
      onClick={!isTeamMemberVariant ? handleClick : undefined}
      onMouseMove={isGlassVariant ? handlePointerMove : undefined}
      onMouseLeave={isGlassVariant ? resetPointerState : undefined}
      onKeyDown={isTeamMemberVariant ? handleKeyDown : undefined}
      role={isTeamMemberVariant ? "button" : undefined}
      tabIndex={isTeamMemberVariant ? 0 : undefined}
      aria-pressed={isTeamMemberVariant ? isFlipped : undefined}
      aria-label={
        isTeamMemberVariant
          ? `${isFlipped ? "Hide" : "Show"} social links for ${ogData.title}`
          : undefined
      }
    >
      {isTeamMemberVariant ? (
        <div className="thumbnail-flip-inner">
          <div
            className="thumbnail-face thumbnail-face--front"
            onClick={handleClick}
          >
            {renderMedia()}

            <div className="thumbnail-content">
              <div className="thumbnail-site-name">{ogData.site_name}</div>
              <h3 className="thumbnail-title">{ogData.title}</h3>
              <p className="thumbnail-description">{ogData.description}</p>
              <div className="thumbnail-flip-hint">Click to reveal socials</div>
            </div>
          </div>

          <div className="thumbnail-face thumbnail-face--back">
            <div className="thumbnail-back-panel" onClick={handleClick}>
              <div className="thumbnail-site-name">Connect</div>

              <div className="thumbnail-social-links">
                {linkedinUrl ? (
                  <a
                    className="thumbnail-social-link"
                    data-no-flip="true"
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleSocialLinkClick}
                    onMouseDown={handleSocialLinkClick}
                    aria-label={`Open ${ogData.title} LinkedIn`}
                  >
                    <FaLinkedinIn />
                  </a>
                ) : null}

                {instagramUrl ? (
                  <a
                    className="thumbnail-social-link"
                    data-no-flip="true"
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleSocialLinkClick}
                    onMouseDown={handleSocialLinkClick}
                    aria-label={`Open ${ogData.title} Instagram`}
                  >
                    <FaInstagram />
                  </a>
                ) : (
                  <span
                    className="thumbnail-social-link thumbnail-social-link--disabled"
                    data-no-flip="true"
                    aria-label="Instagram link coming soon"
                  >
                    <FaInstagram />
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {renderMedia()}

          <div className="thumbnail-content">
            <div className="thumbnail-site-name">{ogData.site_name}</div>
            <h3 className="thumbnail-title">{ogData.title}</h3>
            <p className="thumbnail-description">{ogData.description}</p>
          </div>
        </>
      )}
    </div>
  );
};

export default Thumbnail;
